/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { FunctionComponent } from 'react';
import { styled, Theme } from '@material-ui/core/styles';
import { Typography, Card, CardContent, Box, Grid } from '@material-ui/core';
import Button from '../../buttons/PGE-Button';
import useContentMgmt from '../../../hooks/useContentMgmt';
import EmailTextField from '../../email-text-field';
import PasswordTextField from '../../password-text-field';
import { useIsMobile } from '../../../util/style-utils';
import queryString from 'query-string';
import theme from '../../../themes/theme';
import routes from '../../../routes';
import Link from '../../text-link';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import PasswordIndicatorList from '../../password-indicator-list/PasswordIndicatorList';
import useInstallerForgotPassword from '../../../hooks/pge-plus/useInstallerForgotPassword';
import TextField from '@material-ui/core/TextField';
import { isValidPassword } from '../../../util/form-validation';

const PREFIX = 'InstallerLoginForm';

const classes = {
  card: `${PREFIX}-card`,
  cardContent: `${PREFIX}-cardContent`,
  cardActions: `${PREFIX}-cardActions`,
  hr: `${PREFIX}-hr`,
  submit: `${PREFIX}-submit`,
  paidMessageBox: `${PREFIX}-paidMessageBox`,
};

const Root = styled('div')(() => ({
  [`& .${classes.card}`]: {
    padding: '6em 6em',
    marginTop: '2em',
    [theme.breakpoints.down('sm')]: {
      padding: '4em 2em',
    },
  },
  [`& .${classes.cardContent}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
  },
}));

type Props = {
  isOnboarding?: boolean;
};

type QueryParams = {
  email?: string;
};

const InstallerForgotPassword: FunctionComponent<Props> = ({
  isOnboarding = false,
}) => {
  const { content } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_INSTALLER_FORGOT_PASSWORD,
  );
  const isMobile = useIsMobile();
  const queryParams = queryString.parse(location.search) as QueryParams;
  const {
    emailForm,
    resetForm,
    isResetStep,
    handleSendCode,
    handleReset,
  } = useInstallerForgotPassword({
    defaultEmail: queryParams.email || '',
    genericErrorMessage: content.get('GENERIC_ERROR_NOTIFICATION_MESSAGE')!,
    successMessage: content.get('PASSWORD_RESET_LINK_SENT')!,
  });

  return (
    <Root>
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Typography variant={'h1'} css={{ marginBottom: theme.spacing(1) }}>
            {isOnboarding && content.get('INSTALLER_ONBOARDING_TITLE')}
            {!isOnboarding && content.get('FORGOT_PASSWORD')}
          </Typography>
          {isOnboarding && (
            <Typography css={{ marginBottom: theme.spacing(1) }}>
              {content.get('INSTALLER_ONBOARDING_SUB_TITLE')}
            </Typography>
          )}
          {!isResetStep && (
            <form noValidate>
              <EmailTextField
                name="email"
                label={content.get('REGISTERED_EMAIL_ADDRESS')}
                variant="outlined"
                autoComplete={'email'}
                style={{ width: '100%' }}
                {...emailForm.props('email')}
                InputProps={{
                  disabled: isOnboarding,
                }}
              />
              <Box
                display="flex"
                justifyContent="space-between"
                css={{ marginTop: theme.spacing(3) }}
              >
                <Link
                  plain
                  to={routes.PGE_PLUS_INSTALLER_PORTAL_SIGN_IN}
                  variant={'body2'}
                >
                  {content.get('BACK_TO_SIGNIN')}
                </Link>
                <Button
                  className={classes.submit}
                  type={'submit'}
                  variant={'contained'}
                  color={'primary'}
                  disabled={!emailForm.values.email}
                  onClick={handleSendCode}
                >
                  {content.get('SUBMIT')}
                </Button>
              </Box>
            </form>
          )}

          {isResetStep && (
            <form noValidate>
              <Grid item>
                <Typography>
                  {content.get('INSTALLER_RESET_PASSWORD_INFO')}
                </Typography>
              </Grid>
              <Grid item>
                <EmailTextField
                  name="email"
                  label={content.get('REGISTERED_EMAIL_ADDRESS')}
                  variant="outlined"
                  autoComplete={'email'}
                  disabled
                  style={{ width: '100%' }}
                  {...emailForm.props('email')}
                />
              </Grid>
              <Grid item>
                <TextField
                  name="code"
                  label={content.get('VERIFICATION_CODE')}
                  variant="outlined"
                  fullWidth
                  inputProps={{ maxLength: 10 }}
                  {...resetForm.props('code')}
                />
              </Grid>
              <Grid item>
                <PasswordTextField
                  defaultShow={isMobile}
                  autoComplete="new-password"
                  {...resetForm.props('password')}
                />
              </Grid>
              <Grid item>
                <PasswordIndicatorList password={resetForm.values.password} />
              </Grid>
              <Box
                display="flex"
                justifyContent="space-between"
                css={{ marginTop: theme.spacing(2) }}
              >
                <Link
                  plain
                  to={routes.PGE_PLUS_INSTALLER_PORTAL_SIGN_IN}
                  variant={'body2'}
                >
                  {content.get('BACK_TO_SIGNIN')}
                </Link>
                <Button
                  className={classes.submit}
                  type={'submit'}
                  variant={'contained'}
                  color={'primary'}
                  disabled={
                    !resetForm.values.code ||
                    !isValidPassword(resetForm.values.password)
                  }
                  onClick={handleReset}
                >
                  {content.get('RESET_PASSWORD')}
                </Button>
              </Box>
            </form>
          )}
        </CardContent>
      </Card>
    </Root>
  );
};

export default InstallerForgotPassword;
